import { ThunkAction } from 'redux-thunk';
import { AxiosResponse } from 'axios';
import ApiClient, { createTokenConfig, isCancelled } from 'api/ApiClient';
import { NotificationsAction, showError, alertErrorHandler } from '@spike/notifications-action';
import store, { RootState } from 'store';
import {
    OpsTask,
    OpsTaskInput,
    OpsTaskListResult,
    OpsTasksCounts,
    OpsTasksFilters,
    OpsTaskStatusId
} from 'model/OpsTask';
import { OpsTaskDto, OpsTaskListDto, OpsTasksCountsDto } from './OpsTasksDto';
import { convertOpsTask, convertOpsTaskList, convertOpsTasksCounts, toRequest } from './OpsTasksConverter';
import {
    OpsTasksAction,
    OPS_TASKS_RESET_ACTION_TYPE,
    OPS_TASKS_FETCH_START_ACTION_TYPE,
    OPS_TASKS_FETCH_SUCCESS_ACTION_TYPE,
    OPS_TASKS_FETCH_COUNTS_SUCCESS_ACTION_TYPE,
    OPS_TASKS_SAVE_START_ACTION_TYPE,
    OPS_TASKS_SAVE_SUCCESS_ACTION_TYPE,
    OPS_TASKS_DELETE_START_ACTION_TYPE,
    OPS_TASKS_DELETE_SUCCESS_ACTION_TYPE,
    OPS_TASKS_UPDATE_STATUS_START_ACTION_TYPE,
    OPS_TASKS_UPDATE_STATUS_SUCCESS_ACTION_TYPE,
    OPS_TASKS_BULK_COMPLETE_START_ACTION_TYPE,
    OPS_TASKS_BULK_COMPLETE_SUCCESS_ACTION_TYPE,
    OPS_TASKS_ERROR_ACTION_TYPE
} from './OpsTasksActionsTypes';

type OpsTasksThunk = ThunkAction<void, RootState, null, OpsTasksAction | NotificationsAction>;

const opsTasksUrl = '/ops_tasks';
const DEFAULT_PAGE = 1;
const DEFAULT_SIZE = 25;

const marketplaceId = (): number | undefined => store.getState().login.auth.marketplaceId;
const token = (): string => store.getState().login.auth.token!;

// Translates domain filters into backend query params. counts ignores `status` (it returns every tab).
const filterParams = (filters: OpsTasksFilters, includeStatus: boolean): Array<string> => {
    const params: Array<string> = [];
    if (filters.marketplaceIds && filters.marketplaceIds.length > 0) {
        params.push(`marketplace_ids=${filters.marketplaceIds.join(',')}`);
    }
    if (filters.startDate) {
        params.push(`start_date=${filters.startDate.format('YYYY-MM-DD')}`);
    }
    if (filters.endDate) {
        params.push(`end_date=${filters.endDate.format('YYYY-MM-DD')}`);
    }
    if (filters.roomIds && filters.roomIds.length > 0) {
        params.push(`room_ids=${filters.roomIds.join(',')}`);
    }
    if (filters.staffIds && filters.staffIds.length > 0) {
        params.push(`staff_ids=${filters.staffIds.join(',')}`);
    }
    if (includeStatus && filters.status) {
        params.push(`status=${filters.status}`);
    }
    if (filters.query && filters.query.trim().length > 0) {
        params.push(`query=${encodeURIComponent(filters.query.trim())}`);
    }
    if (filters.sortBy) {
        params.push(`sort_by=${filters.sortBy}`);
    }
    if (filters.sortOrder) {
        params.push(`sort_order=${filters.sortOrder}`);
    }
    return params;
};

const buildUrl = (path: string, parts: Array<string>): string => `${path}?${parts.join('&')}`;

export const fetchOpsTasksThunk = (
    filters: OpsTasksFilters = {},
    page: number = DEFAULT_PAGE,
    size: number = DEFAULT_SIZE
): OpsTasksThunk => {
    return async dispatch => {
        dispatch(fetchStart(filters, page, size));

        const url = buildUrl(opsTasksUrl, [
            `marketplace_id=${marketplaceId()}`,
            ...filterParams(filters, true),
            `page=${page}`,
            `size=${size}`
        ]);

        try {
            const response: AxiosResponse<OpsTaskListDto> = await ApiClient.get(url, createTokenConfig(token()));
            dispatch(fetchSuccess(convertOpsTaskList(response.data)));
        } catch (apiError) {
            if (!isCancelled(apiError)) {
                dispatch(error());
                dispatch(showError('Error fetching tasks.'));
            }
        }
    };
};

export const fetchOpsTasksCountsThunk = (filters: OpsTasksFilters = {}): OpsTasksThunk => {
    return async dispatch => {
        const url = buildUrl(`${opsTasksUrl}/counts`, [
            `marketplace_id=${marketplaceId()}`,
            ...filterParams(filters, false)
        ]);

        try {
            const response: AxiosResponse<OpsTasksCountsDto> = await ApiClient.get(url, createTokenConfig(token()));
            dispatch(fetchCountsSuccess(convertOpsTasksCounts(response.data)));
        } catch (apiError) {
            if (!isCancelled(apiError)) {
                dispatch(showError('Error fetching task counts.'));
            }
        }
    };
};

// Re-pulls list + counts with the last-used filters so both views reconcile after a mutation.
const reconcileOpsTasksThunk = (): OpsTasksThunk => {
    return dispatch => {
        setTimeout(() => {
            const { filters, page, size } = store.getState().opsTasks;
            dispatch(fetchOpsTasksThunk(filters, page, size));
            dispatch(fetchOpsTasksCountsThunk(filters));
        }, 500);
    };
};

export const saveOpsTaskThunk = (input: OpsTaskInput): OpsTasksThunk => {
    return async dispatch => {
        dispatch(saveStart());

        const apiClientMethod = input.id ? ApiClient.patch : ApiClient.post;
        const url = input.id
            ? `${opsTasksUrl}/${input.id}?marketplace_id=${marketplaceId()}`
            : `${opsTasksUrl}?marketplace_id=${marketplaceId()}`;

        try {
            const response: AxiosResponse<OpsTaskDto> = await apiClientMethod(
                url,
                toRequest(input),
                createTokenConfig(token())
            );
            dispatch(saveSuccess(convertOpsTask(response.data)));
            dispatch(reconcileOpsTasksThunk());
        } catch (apiError) {
            if (!isCancelled(apiError)) {
                dispatch(error());
                alertErrorHandler(apiError, dispatch, 'Error saving task.');
            }
        }
    };
};

export const deleteOpsTaskThunk = (id: number): OpsTasksThunk => {
    return async dispatch => {
        dispatch(deleteStart(id));

        const url = `${opsTasksUrl}/${id}?marketplace_id=${marketplaceId()}`;

        try {
            await ApiClient.delete(url, createTokenConfig(token()));
            dispatch(deleteSuccess(id));
            dispatch(reconcileOpsTasksThunk());
        } catch (apiError) {
            if (!isCancelled(apiError)) {
                dispatch(error());
                alertErrorHandler(apiError, dispatch, 'Error deleting task.');
                // Restore the optimistically removed row from the server.
                dispatch(reconcileOpsTasksThunk());
            }
        }
    };
};

export const updateOpsTaskStatusThunk = (id: number, status: OpsTaskStatusId): OpsTasksThunk => {
    return async dispatch => {
        dispatch(updateStatusStart(id, status));

        const url = `${opsTasksUrl}/${id}?marketplace_id=${marketplaceId()}`;

        try {
            const response: AxiosResponse<OpsTaskDto> = await ApiClient.patch(
                url,
                { ops_task: { status } },
                createTokenConfig(token())
            );
            dispatch(updateStatusSuccess(convertOpsTask(response.data)));
            // A status change can move the task between tabs → reconcile list + counts.
            dispatch(reconcileOpsTasksThunk());
        } catch (apiError) {
            if (!isCancelled(apiError)) {
                dispatch(error());
                alertErrorHandler(apiError, dispatch, 'Error updating task status.');
                // Revert the optimistic change with authoritative data.
                dispatch(reconcileOpsTasksThunk());
            }
        }
    };
};

export const bulkCompleteOpsTasksThunk = (taskIds: Array<number>): OpsTasksThunk => {
    return async dispatch => {
        dispatch(bulkCompleteStart());

        const url = `${opsTasksUrl}/bulk_complete?marketplace_id=${marketplaceId()}`;

        try {
            await ApiClient.patch(url, { task_ids: taskIds }, createTokenConfig(token()));
            dispatch(bulkCompleteSuccess());
            dispatch(reconcileOpsTasksThunk());
        } catch (apiError) {
            if (!isCancelled(apiError)) {
                dispatch(error());
                alertErrorHandler(apiError, dispatch, 'Error completing tasks.');
            }
        }
    };
};

//Action creators
export const reset = (): OpsTasksAction => ({
    type: OPS_TASKS_RESET_ACTION_TYPE
});

const fetchStart = (filters: OpsTasksFilters, page: number, size: number): OpsTasksAction => ({
    type: OPS_TASKS_FETCH_START_ACTION_TYPE,
    payload: { filters, page, size }
});

const fetchSuccess = (result: OpsTaskListResult): OpsTasksAction => ({
    type: OPS_TASKS_FETCH_SUCCESS_ACTION_TYPE,
    payload: { result }
});

const fetchCountsSuccess = (counts: OpsTasksCounts): OpsTasksAction => ({
    type: OPS_TASKS_FETCH_COUNTS_SUCCESS_ACTION_TYPE,
    payload: { counts }
});

const saveStart = (): OpsTasksAction => ({
    type: OPS_TASKS_SAVE_START_ACTION_TYPE
});

const saveSuccess = (savedTask: OpsTask): OpsTasksAction => ({
    type: OPS_TASKS_SAVE_SUCCESS_ACTION_TYPE,
    payload: { savedTask }
});

const deleteStart = (deletedTaskId: number): OpsTasksAction => ({
    type: OPS_TASKS_DELETE_START_ACTION_TYPE,
    payload: { deletedTaskId }
});

const deleteSuccess = (deletedTaskId: number): OpsTasksAction => ({
    type: OPS_TASKS_DELETE_SUCCESS_ACTION_TYPE,
    payload: { deletedTaskId }
});

const updateStatusStart = (taskId: number, status: OpsTaskStatusId): OpsTasksAction => ({
    type: OPS_TASKS_UPDATE_STATUS_START_ACTION_TYPE,
    payload: { taskId, status }
});

const updateStatusSuccess = (task: OpsTask): OpsTasksAction => ({
    type: OPS_TASKS_UPDATE_STATUS_SUCCESS_ACTION_TYPE,
    payload: { task }
});

const bulkCompleteStart = (): OpsTasksAction => ({
    type: OPS_TASKS_BULK_COMPLETE_START_ACTION_TYPE
});

const bulkCompleteSuccess = (): OpsTasksAction => ({
    type: OPS_TASKS_BULK_COMPLETE_SUCCESS_ACTION_TYPE
});

const error = (message?: string): OpsTasksAction => ({
    type: OPS_TASKS_ERROR_ACTION_TYPE,
    payload: { error: message }
});
